import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { persistAttributionToProfile } from '../lib/attribution';
import { API_BASE } from '../lib/apiConfig';
import {
  phOtpVerified,
  phSignupCompleted,
  phLoginCompleted,
  phFailure,
} from '../lib/posthog';
import { trackCompleteRegistration } from '../lib/fbpixel';
import { trackRedditSignUp } from '../lib/redditPixel';
import AuthStepper from '../components/AuthStepper';
import AuthHelp from '../components/AuthHelp';
import '@styles/pages/verify.css';

// How many digits Supabase puts in the code. It has to match "Email OTP
// length" under Authentication > Providers > Email in the Supabase
// dashboard, or every code looks wrong. Supabase sends 8, so 8 it is.
// (The 2026-10-09 switch to 6 broke sign-in: the dashboard was still on
// 8, so the email had two more digits than the screen had boxes.) Set
// VITE_OTP_LENGTH to change it without touching this file.
const OTP_LENGTH = Number(import.meta.env.VITE_OTP_LENGTH) || 8;

export default function Verify() {
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();

  const email = useMemo(() => {
    const fromQuery = params.get('email');
    const fromState = location.state?.email;
    const fromSession = sessionStorage.getItem('signup_email');
    return (fromQuery || fromState || fromSession || '').trim().toLowerCase();
  }, [params, location.state]);

  const [code, setCode] = useState<string[]>(
    Array.from({ length: OTP_LENGTH }, () => ''),
  );
  const [error, setError] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [resent, setResent] = useState(false);
  const inputsRef = useRef<HTMLInputElement[]>([]);

  /* Persist email across refresh */
  useEffect(() => {
    if (email) sessionStorage.setItem('signup_email', email);
  }, [email]);

  /* Re-hydrate checkout context when the user opens the magic link on
     a DIFFERENT device (paid on laptop, opens email on phone). Signup.tsx
     appends ref + plan to the verify URL so checkout_ref is available for
     subscription linking below even with empty localStorage. */
  useEffect(() => {
    const refFromUrl = params.get('ref');
    const planFromUrl = params.get('plan');
    if (refFromUrl && !localStorage.getItem('checkout_ref')) {
      localStorage.setItem('checkout_ref', refFromUrl);
    }
    if (planFromUrl && !localStorage.getItem('checkout_plan')) {
      localStorage.setItem('checkout_plan', planFromUrl);
    }
  }, [params]);

  /* Autofocus first input */
  useEffect(() => {
    inputsRef.current[0]?.focus();
  }, []);

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number,
  ) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  /* ===============================
     VERIFY OTP
  =============================== */
  /* `typed` is the code straight from the keystroke or paste that
     completed it, since the state hasn't caught up yet at that point. */
  const handleVerify = async (typed?: string) => {
    if (verifying) return;
    setError(null);
    setResent(false);

    if (!email) {
      setError('We lost track of your email. Go back and enter it again.');
      return;
    }

    const token = typed ?? code.join('');
    if (token.length !== OTP_LENGTH) {
      setError(`Enter all ${OTP_LENGTH} digits of the code we emailed you.`);
      return;
    }

    setVerifying(true);
    const { error: verifyError } = await supabase.auth.verifyOtp({
      email,
      token,
      type: 'email',
    });

    if (verifyError) {
      setVerifying(false);
      // 2026-08-08: verification failures fired nothing, so a bad-code or
      // expired-code spike was indistinguishable from users abandoning.
      phFailure({
        step: 'otp_verify',
        message: verifyError.message,
        code: (verifyError as { status?: number }).status,
      });
      // Supabase says "Token has expired or is invalid" for a wrong code,
      // an old code and a used code alike. Say what to do instead, and
      // clear the boxes so the new code can go straight in.
      setError("That code didn't work. It may have expired or been replaced by a newer one. Send a new code below and use the latest email.");
      setCode(Array.from({ length: OTP_LENGTH }, () => ''));
      inputsRef.current[0]?.focus();
      return;
    }

    const { data: { user: verifiedUser } } = await supabase.auth.getUser();

    /* ⚠️ 2026-08-08 — REGISTRATION vs LOGIN.
       /verify is the shared OTP screen for both Signup.tsx and Login.tsx, and
       this block used to fire Meta CompleteRegistration + Reddit SignUp after
       ANY successful verifyOtp. The dedup key inside trackCompleteRegistration
       is sessionStorage-scoped, so a returning customer logging in next week
       fired a brand-new CompleteRegistration to both the pixel and CAPI. Meta
       has therefore been optimizing signup campaigns against a conversion
       signal contaminated with returning-user logins.

       Two independent gates now have to agree before we call it a
       registration:
         1. intent — the originating page says so (?intent=signup). Login now
            sends ?intent=login.
         2. account age — Supabase user.created_at is within the last 10
            minutes. This is the backstop for magic-link opens and any path
            that loses the query param, and it is what makes the gate safe by
            default: with no intent param at all, an old account still reads
            as a login.
       Plus a permanent per-user localStorage guard, so one account can never
       produce a second CompleteRegistration on this device. */
    const declaredIntent = params.get('intent');
    const accountAgeMs = verifiedUser?.created_at
      ? new Date().getTime() - new Date(verifiedUser.created_at).getTime()
      : Number.POSITIVE_INFINITY;
    const isFreshAccount = accountAgeMs < 10 * 60 * 1000;
    const isRegistration =
      declaredIntent === 'signup' ||
      (declaredIntent !== 'login' && isFreshAccount);

    // Track successful verification, now with the intent attached so the two
    // flows are separable in PostHog.
    phOtpVerified(email, isRegistration ? 'signup' : 'login');
    if (isRegistration) {
      phSignupCompleted({ email, method: 'otp' });
    } else {
      phLoginCompleted({ email, method: 'otp' });
    }

    if (verifiedUser && isRegistration) {
      const regGuardKey = `registration_tracked:${verifiedUser.id}`;
      let alreadyTracked = false;
      try {
        alreadyTracked = Boolean(localStorage.getItem(regGuardKey));
        if (!alreadyTracked) localStorage.setItem(regGuardKey, '1');
      } catch {
        /* localStorage unavailable — fall through and fire */
      }

      if (!alreadyTracked) {
        // Meta: fire CompleteRegistration via both pixel and Conversions API.
        // We use the user's id as event_id so dedup works across runs.
        // Don't await, tracking should never block navigation
        void trackCompleteRegistration({
          eventID: verifiedUser.id,
          email,
          externalId: verifiedUser.id,
        });

        // Reddit: SignUp is Reddit's CompleteRegistration equivalent. The CAPI
        // leg already fires from /api/conversion via the Meta→Reddit mapping,
        // but the pixel leg was never wired, so Events Manager showed SignUp as
        // CAPI-only (0 pixel events, 2026-07-27). Same eventID as the CAPI fire
        // so Reddit collapses the two into one conversion.
        trackRedditSignUp({
          eventID: verifiedUser.id,
          email,
          externalId: verifiedUser.id,
        });
      }
    }

    // Cleanup
    sessionStorage.removeItem('signup_email');

    /* ===============================
       LINK SUBSCRIPTION
       1) Primary: link via checkout_ref (works even if emails differ)
       2) Fallback: email-based RLS linking (existing mechanism)
    =============================== */
    const { data: { user } } = await supabase.auth.getUser();

    if (user) {
      // Persist first-touch attribution to the profile the moment the account
      // exists — before onboarding — so the source isn't lost if the user
      // drops off mid-onboarding. Fire-and-forget; never blocks navigation.
      void persistAttributionToProfile(user.id);

      let linked = false;

      // Primary: checkout_ref linking via server endpoint
      const checkoutRef = localStorage.getItem('checkout_ref');
      if (checkoutRef) {
        try {
          const session = await supabase.auth.getSession();
          const token = session.data?.session?.access_token;

          if (token) {
            const res = await fetch(`${API_BASE}/stripe/link-subscription`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({ checkout_ref: checkoutRef }),
            });

            if (res.ok) {
              console.log('✅ Subscription linked via checkout_ref');
              linked = true;
            }
          }
        } catch {
          // Falls through to email-based fallback
        }
      }

      // Fallback: email-based RLS linking
      if (!linked) {
        try {
          await supabase
            .from('subscriptions')
            .update({ user_id: user.id })
            .is('user_id', null)
            .select('id');
        } catch {
          // Non-critical, AppGate will handle routing
        }
      }

      // Clear checkout_ref regardless of outcome
      localStorage.removeItem('checkout_ref');
    }

    // Let AppGate handle routing based on user's actual state
    navigate('/', { replace: true });
  };

  /* These two sit below handleVerify because they call it when the
     last digit lands. */
  const handleChange = (value: string, index: number) => {
    if (!/^\d?$/.test(value)) return;

    const next = [...code];
    next[index] = value;
    setCode(next);

    if (value && index < OTP_LENGTH - 1) {
      inputsRef.current[index + 1]?.focus();
    }

    // Last digit in: no need to make them press Continue.
    if (next.every((d) => d !== '')) {
      void handleVerify(next.join(''));
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    const pasted = e.clipboardData
      .getData('text')
      .replace(/\D/g, '')
      .slice(0, OTP_LENGTH);

    if (pasted.length !== OTP_LENGTH) return;

    e.preventDefault();
    setCode(pasted.split(''));
    inputsRef.current[OTP_LENGTH - 1]?.focus();
    void handleVerify(pasted);
  };

  /* ===============================
     RESEND CODE
  =============================== */
  const resendCode = async () => {
    setError(null);
    setResent(false);

    if (!email) {
      setError('We lost track of your email. Go back and enter it again.');
      return;
    }

    const { error } = await supabase.auth.signInWithOtp({
      email,
      // Resend is only ever for an account that already exists — without
      // this flag a mistyped ?email= here could mint a user out of thin
      // air, which is exactly the hole /signup closed on 2026-08-26.
      options: { shouldCreateUser: false },
    });
    if (error) {
      setError(
        (error as { status?: number }).status === 429
          ? 'We just sent one. Give it a minute, then try again.'
          : "We couldn't send a new code. Try again in a moment.",
      );
      return;
    }
    setCode(Array.from({ length: OTP_LENGTH }, () => ''));
    inputsRef.current[0]?.focus();
    setResent(true);
  };

  return (
    <div className="verify-container">
      <header className="auth-header">
        <a
          href="https://mediloop.ai"
          className="logo"
          aria-label="Go to Mediloop homepage"
        >
          <img
            src="/images/logos/tail-logo.png"
            alt="Mediloop"
            className="logo-img"
            width={36}
            height={36}
          />
        </a>
      </header>

      <main className="verify-card">
        <AuthStepper currentStep="verify" />
        <h1>Check <span className="italic">your email.</span></h1>
        <p className="verify-subtitle">
          We sent {OTP_LENGTH === 8 || OTP_LENGTH === 11 ? 'an' : 'a'} {OTP_LENGTH}-digit code to{' '}
          <strong>{email || 'your email'}</strong>. You can paste it in.
        </p>

        <div
          className="code-inputs"
          onPaste={handlePaste}
          role="group"
          aria-label={`Enter ${OTP_LENGTH}-digit verification code`}
        >
          {code.map((digit, index) => (
            <input
              key={index}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              ref={(el) => {
                if (el) inputsRef.current[index] = el;
              }}
              onChange={(e) => handleChange(e.target.value, index)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              aria-label={`Digit ${index + 1} of ${OTP_LENGTH}`}
              autoComplete="one-time-code"
            />
          ))}
        </div>

        {error && <p className="verify-error" role="alert" aria-live="polite">{error}</p>}

        {resent && !error && (
          <p className="verify-footer" role="status" aria-live="polite">
            New code sent. Use the latest email.
          </p>
        )}

        <button className="verify-primary" onClick={() => handleVerify()} disabled={verifying}>
          {verifying ? 'Checking…' : 'Continue'}
        </button>

        <p className="verify-footer">
          Nothing after a minute? Check spam, or{' '}
          <button className="link-btn" onClick={resendCode}>
            send a new code
          </button>
          .
        </p>
        <p className="verify-footer">
          Wrong email?{' '}
          <button className="link-btn" onClick={() => navigate('/login')}>
            Go back
          </button>
        </p>
        <AuthHelp page="verify" email={email} label="Code not working?" />
      </main>
    </div>
  );
}
